import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { TOKEN_PROGRAM_ID, getAssociatedTokenAddressSync } from '@solana/spl-token';
import { Config, getAccount, readContracts, switchChain } from "@wagmi/core";
import { BehaviorSubject, debounceTime } from "rxjs";
import { Address, formatEther, formatUnits } from "viem";

import { environment } from "../../../environments/environment";
import { CHAINS } from "../../shared/constants/app-enums";
import { ethUsdcContract, ethUsdtContract, isSolanaActivated, multicallEthContract, presaleContractEth, skipIndexUpdate } from "../../shared/constants/wallet-connect.const";
import { IBalnceDataEVM, IBalnceDataSOL, ITokenomics } from "../../shared/interfaces/wallet.interface";
import { AnalyticsService } from "../analytics.service";
import { PopupService } from "../popup.service";
import { SolanaInitialisationService } from "../sol-services/solana-initialise.service";
import { ReownService } from "./reown.service";


@Injectable({
    providedIn: 'root',
})


export class PresaleDataService {
    private refreshRate: number = 10000;
    private oldWalletAddress: string = "";
    private isSolIntilised: boolean = false

    public refreshId!: NodeJS.Timeout;
    public wagmiConfig!: Config;
    public walletAddress!: string;
    public walletAddressSOL!: string;
    public localStep: number = 0;
    public chainId!: number; // don't change this untill it is coming from reown service
    public balanceDataEVM: IBalnceDataEVM = {} as IBalnceDataEVM;
    public balanceDataSOl: IBalnceDataSOL = {} as IBalnceDataSOL;
    public $balanceData = new BehaviorSubject<{ sol: IBalnceDataSOL, evm: IBalnceDataEVM }>({ evm: {} as IBalnceDataEVM, sol: {} as IBalnceDataSOL });
    public $widgetBalanceData = new BehaviorSubject<{ sol: IBalnceDataSOL, evm: IBalnceDataEVM }>({ evm: {} as IBalnceDataEVM, sol: {} as IBalnceDataSOL });
    public $walletAddress = new BehaviorSubject<string>("");
    public tokenomics: ITokenomics[] = [];
    public userStaked: number = 0
    public stakeApy: number = 0
    public activeChain: string = CHAINS.EVM;
    public saleToken: Address = "" as Address;
    public connectedWalletName: string = "";
    public isReferalAvailable: boolean = false;
    public percentageReferral: number = 0;

    constructor(
        private reownService: ReownService,
        private solService: SolanaInitialisationService,
        private analyticsService: AnalyticsService,
        private popupService: PopupService,
        private translateService: TranslateService,
        private http: HttpClient
    ) {
        this.reownService.$connectionItems.pipe(
            debounceTime(200)
        ).subscribe((item) => {
            this.wagmiConfig = item.wagmiConfig;
            if (this.wagmiConfig) {
                clearTimeout(this.refreshId);
                this.getPresalesData(this.isSolIntilised);
                this.isSolIntilised = false;
            }

            if (item.activeChain) this.activeChain = item.activeChain;
            if (item.wagmiConfig && item.walletAddressEVM !== "" && !this.walletAddress) {
                this.walletAddress = item.walletAddressEVM as string
                this.$walletAddress.next(this.walletAddress);
                this.connectedWalletName = this.reownService.connectedWalletName;
                clearTimeout(this.refreshId)
                this.setData();
                this.getPresalesData(true);
            }
            if (item && item.walletAddressSOL !== '' && !this.walletAddressSOL) {
                this.walletAddressSOL = item.walletAddressSOL as string
                this.$walletAddress.next(this.walletAddressSOL);
                this.connectedWalletName = this.reownService.connectedWalletName;
                clearTimeout(this.refreshId)
                this.getPresalesData(true);
            }
        });
        this.reownService.$walletDisconnected.subscribe((item) => {
            if (item) {
                this.setData();
                this.analyticsService.referUrl = '';
                this.walletAddress = "";
                this.walletAddressSOL = "";
                this.$walletAddress.next("");
                this.connectedWalletName = "";
                this.$balanceData.next({ evm: this.balanceDataEVM, sol: this.balanceDataSOl })
            }
        });
        this.solService.$solInitilisation.subscribe((item) => {
            if (item && this.wagmiConfig?.storage) {
                this.isSolIntilised = item
                clearTimeout(this.refreshId);
                this.getPresalesData(true);
            }
        })
    }

    async getPresalesData(isSolInilized = false) {
        if (!this.wagmiConfig) return;

        let _solBalanceData: IBalnceDataSOL = {} as IBalnceDataSOL;
        await this.prepareInitialWidgetLoadItems(isSolInilized);
        const currentStep = this.balanceDataEVM.currentStep;

        const ethBalanceData = await this.prepareETHContractData();

        this.isReferalAvailable = false
        this.percentageReferral = 0;



        // /* assigning the value of total token purchased by the current user */
        this.balanceDataEVM.ethClaimable = ethBalanceData.ethClaimable;
        this.balanceDataEVM.totalClaimable = (this.balanceDataEVM.ethClaimable || 0);
        this.balanceDataEVM = { ...this.balanceDataEVM, ...ethBalanceData }

        if (isSolInilized) {
            _solBalanceData = await this.getPresaleDataSOL();
            const data = await this.getSolprice();
            const noOfTokenForOneSOl = data.Price / +this.tokenomics[currentStep]?.tokenUSDT;
            _solBalanceData.oneSol = 1 / noOfTokenForOneSOl;
            const availableToken = this.tokenomics[currentStep]?.maxToken - _solBalanceData.totalTokensSoldSOL;
            _solBalanceData.maxTokensToBuySOL = availableToken > _solBalanceData.tokenBuyLimit ? _solBalanceData.tokenBuyLimit : availableToken;
            _solBalanceData.solUsdt = +this.tokenomics[currentStep]?.tokenUSDT * Math.pow(10, 6) || 0;
        }
        this.balanceDataSOl = _solBalanceData

        this.$balanceData.next({ evm: this.balanceDataEVM, sol: this.balanceDataSOl });
        this.$widgetBalanceData.next({ evm: this.balanceDataEVM, sol: this.balanceDataSOl });

        if (this.reownService.sendVwoData && (this.walletAddress || this.walletAddressSOL)) {
            this.analyticsService.vwoData('walletConnectionSuccessful', {
                walletAddress: this.walletAddress,
                connectionType: this.connectedWalletName,
                ethBalance: this.balanceDataEVM.nativeBalETH,
                bnbBalance: this.balanceDataEVM.nativeBalBSC,
                solBalance: 0,
                stablecoinBalanceEth: this.balanceDataEVM.usdtBalETH + this.balanceDataEVM.usdtBalBSC,
                stablecoinBalanceBsc: this.balanceDataEVM.usdcBalETH + this.balanceDataEVM.usdcBalBSC,
                stablecoinBalanceSol: 0,
            });
            this.reownService.sendVwoData = false;
        }

        // if (this.walletAddress && this.isSoldOut) this.getMetaData();
        if (this.refreshId) clearTimeout(this.refreshId);
        this.refreshId = setTimeout(() => this.getPresalesData(isSolInilized), this.refreshRate); // Call the self method on each 5secs interval
    }

    // usd raised, tokenssold, tokenomics preparation for initial widget loading
    async prepareInitialWidgetLoadItems(solInit = false) {
        const smartContract = {
            contracts: [

                {
                    ...presaleContractEth,
                    functionName: 'currentStep',
                    chainId: environment.eth.chainIdInt, // 0
                    args: [],
                },
                {
                    ...presaleContractEth,
                    functionName: 'usdRaised', // this method is returning the total USDT raised via BSC token.
                    chainId: environment.eth.chainIdInt, // 1
                    args: [],
                },
                {
                    ...presaleContractEth, // 2
                    functionName: 'trackRemainingTokens',
                    chainId: environment.eth.chainIdInt,
                    args: [],
                },
                {
                    ...presaleContractEth,
                    functionName: 'roundDetails',
                    chainId: environment.eth.chainIdInt, // 3 ROUND END DATES
                    args: [2],
                },
                {
                    ...presaleContractEth,
                    functionName: 'roundDetails',
                    chainId: environment.eth.chainIdInt, //4 MAX TOKENS
                    args: [0],
                },
                {
                    ...presaleContractEth,
                    functionName: 'roundDetails',
                    chainId: environment.eth.chainIdInt, //5 TOKEN USDT
                    args: [1],
                },
                {
                    ...presaleContractEth,
                    functionName: 'totalTokensSold', // this method is returning the no of tokens sold via BSC
                    chainId: environment.eth.chainIdInt, // 6
                    args: [],
                },
            ],
        } as const;

        /**
         * Above contracts are going to be read.
         */
        const _presaleData: any = await readContracts(this.wagmiConfig, smartContract);
        // console.log('\n******presaleData =', _presaleData, '\n******\n');


        const currentStep = Number(_presaleData[0].result);
        if (currentStep >= this.localStep) {
            this.localStep = currentStep;
            this.balanceDataEVM.currentStep = currentStep;
        } else {
            this.balanceDataEVM.currentStep = this.localStep;
        }
        this.balanceDataEVM.usdRaisedETH = +formatEther(_presaleData[1].result || 0)
        this.balanceDataEVM.totalUsdRaised = this.balanceDataEVM.usdRaisedETH
        this.balanceDataEVM.ethTokensSold = Number(_presaleData[6].result);
        this.balanceDataEVM.totalTokensSold = this.balanceDataEVM.ethTokensSold;
        let solUnsoldArr = []
        if (solInit) {
            const stateInfo = await this.solService.program.account.stateInfo.fetch(this.solService.stateInfoAddress[0]); // this.stateInfoAddress[0] = Public Key for State Info
            this.balanceDataSOl.totalTokensSoldSOL = Number(stateInfo.tokensSold) + 1;
            this.balanceDataSOl.usdRaised = Number(stateInfo.totalUsdRaised) / 1000000 + 1;
            const soldArray = stateInfo?.tokensLeftUnsold?.toString()?.split(',');
            const skipIndexSol: Array<any> = [];
            solUnsoldArr = skipIndexUpdate(
                soldArray.map((val: any) => Number(val)),
                skipIndexSol,
            );
        }
        this.tokenomics = await this.prepareTokenomics(_presaleData, currentStep, solUnsoldArr);
        this.balanceDataEVM.totalUsdRaised = (this.balanceDataEVM.usdRaisedETH || 0) + (this.balanceDataEVM.usdRaisedBSC || 0)
        // console.log('\n******presaleData =', this.balanceDataEVM, '\n******\n');

        this.$widgetBalanceData.next({ evm: this.balanceDataEVM, sol: this.balanceDataSOl })
    }

    async prepareTokenomics(presaleData: any, currentStep: number, solUnsoldArr: number[] = []): Promise<ITokenomics[]> {
        const skipIndexEth: Array<any> = [];
        const ethUnsoldArr = skipIndexUpdate(presaleData[2].result, skipIndexEth);
        let tokenomics: ITokenomics[] = [];
        const tokenomics2 = [];
        const _isSolanaActivated = isSolanaActivated(this.reownService.walletConnector.chainNamespaces)
        let newCounter = 0;
        for (let time of presaleData[3].result) {
            let minToken = newCounter == 0 ? 0 : Number(presaleData[4].result[newCounter - 1]);
            let maxToken = Number(presaleData[4].result[newCounter]);
            let tokenUSDT = +formatEther(presaleData[5].result[newCounter]);
            let roundInfo: ITokenomics = {
                minToken,
                maxToken,
                maxAmount: newCounter == 0 ? (maxToken - minToken) * tokenUSDT : (maxToken - minToken) * tokenUSDT + tokenomics2[newCounter - 1].maxAmount,
                tokenUSDT,
                title: 'widget.round_title',
                endDate: Number(time) * 1000,
            };
            // console.log(roundInfo)
            if (newCounter < currentStep) {
                const ethVal = ethUnsoldArr[newCounter] || 0;
                const solVal = solUnsoldArr[newCounter] || 0;
                // if SOL chain is not enabled then do it 1 * {roundUnsoldCode}
                let roundUnSold = (_isSolanaActivated ? 1 : 0) * (roundInfo?.maxToken - roundInfo?.minToken) - (ethVal + solVal);
                roundUnSold = roundUnSold * roundInfo.tokenUSDT;
                roundInfo.maxAmount = roundInfo.maxAmount + roundUnSold;
            }
            // roundInfo.maxAmount = Math.ceil(roundInfo.maxAmount)
            tokenomics2.push(roundInfo);
            newCounter++;
        }
        tokenomics = tokenomics2;
        tokenomics = [...tokenomics, tokenomics[tokenomics.length - 1]]; // cloned
        return Promise.resolve(tokenomics)
    }

    async prepareETHContractData(): Promise<IBalnceDataEVM> {
        const ethBalanceData: IBalnceDataEVM = {} as IBalnceDataEVM
        const smartContract = {
            contracts: [
                // =========== ETH Contract ==============
                {
                    ...presaleContractEth, // MAx token allowed to buy
                    functionName: 'maxTokensToBuy', // 0
                    chainId: environment.eth.chainIdInt,
                    args: [],
                },
                {
                    ...presaleContractEth,
                    functionName: 'userDeposits', // this method is returning the total token purchased by current user via ETH network.
                    chainId: environment.eth.chainIdInt, // 1
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...multicallEthContract,
                    functionName: 'getEthBalance', // this method is returning the eth balance of user via multicall contract.
                    chainId: environment.eth.chainIdInt, // 2
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...ethUsdtContract,
                    functionName: 'balanceOf', // this method is returning the usdt balance on ETH network.
                    chainId: environment.eth.chainIdInt,  // 3
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...presaleContractEth, // To get the token address dynamically
                    functionName: 'saleToken', // 4
                    chainId: environment.eth.chainIdInt,
                    args: [],
                },
                {
                    ...ethUsdcContract,
                    functionName: 'balanceOf', // this method is returning the usdc balance on ETH network.
                    chainId: environment.eth.chainIdInt, // 5
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...presaleContractEth,
                    functionName: 'checkReferralStatus', // this method is returning the referral status on ETH network.
                    chainId: environment.eth.chainIdInt, // 6
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                }
            ],
        } as const;

        const presaleData: any = await readContracts(this.wagmiConfig, smartContract)
        // console.log('ETH Presale Data:', presaleData);

        if (this.walletAddress) {
            ethBalanceData.nativeBalETH = +formatEther(presaleData[2]?.result || 0);
            ethBalanceData.usdtBalETH = +formatUnits(presaleData[3].result, 6);
            ethBalanceData.usdcBalETH = +formatUnits(presaleData[5].result, 6);
        } else {
            ethBalanceData.usdtBalETH = 0;
            ethBalanceData.nativeBalETH = 0;
            ethBalanceData.usdcBalETH = 0;
        }

        /* assigning the value of no of token sold va bsc and eth contract */
        ethBalanceData.maxTokensToBuyEVM = Number(presaleData[0].result);
        this.saleToken = presaleData[4].result
        ethBalanceData.isReferalAvailable = !!Number(presaleData[6].result);
        ethBalanceData.percentageReferral = presaleData[6].result ? Number(presaleData[6].result) / 1000 : 0;

        ethBalanceData.buyHelprETH = 0; //reset value
        ethBalanceData.usdtBuyHelperETH = 0; //reset value

        /* assigning the value of total token purchased by the current user */
        ethBalanceData.ethClaimable = +formatEther(presaleData[1].result || 0);
        return Promise.resolve(ethBalanceData)
    }

    setData(): void {
        const wagmiData = getAccount(this.wagmiConfig);
        this.chainId = wagmiData.chainId as number;
        this.refreshRate = wagmiData.address ? 10000 : 20000;
        setTimeout(() => {
            if (wagmiData.address && wagmiData.address != this.oldWalletAddress) {
                this.oldWalletAddress = wagmiData.address;
                this.analyticsService.sendDashFx(this.walletAddress);
                this.analyticsService.windowDataLayer('connectWallet', 'successful', 2, 1, 0, '', '', 0, 0, this.walletAddress, "", this.balanceDataEVM, this.chainId, this.tokenomics[this.balanceDataEVM.currentStep].tokenUSDT);
            }
        }, 2000);
    }

    /**
  * switching the current network. If user is on wrong network
  * and got error on switching then a popup
  * will appear saying to connect correct chain
  */
    async switchNetwork(chainId: number): Promise<void> {
        const oldChainId = this.chainId;
        const newChainId = chainId;
        this.analyticsService.vwoData('networkSwitch', { networkSwitchInitiated: true, networkSwitchCompleted: false, oldChainId, newChainId });

        await switchChain(this.wagmiConfig, {
            chainId: chainId,
        }).then(
            (success: any) => {
                // this.appRef.tick();
                this.chainId = chainId;
                this.analyticsService.vwoData('networkSwitch', { networkSwitchInitiated: false, networkSwitchCompleted: true, oldChainId, newChainId });
            },
            (err: any) => {
                const chain = environment.eth;
                const chainName = chain.chainInfo.params[0].chainName;
                this.popupService.messagePopup({ icon: 'info', desc: this.translateService.instant('popup.switchNetwork', { chain_name: chainName }), confirmBtnTxt: this.translateService.instant('misc.ok') });
            },
        );
    }


    // SOL BALANCE AREA //

    async getPresaleDataSOL(): Promise<IBalnceDataSOL> {
        const _balanceData: IBalnceDataSOL = {} as IBalnceDataSOL;
        try {
            let _balanceDataConnected: IBalnceDataSOL = {} as IBalnceDataSOL;
            // const accountInfo = await this.solService.program.account.presaleAccount.fetch(this.solService.presaleAccountAddress[0]); // this.presaleAccountAddress[0] = Public Key for Presale Account Address
            const stateInfo = await this.solService.program.account.stateInfo.fetch(this.solService.stateInfoAddress[0]); // this.stateInfoAddress[0] = Public Key for State Info

            // // paymentWalletUsdtAccount - The SPL token account that receives USDT payments
            // // paymentWalletSolAccount -	A wallet address (public key) that receives SOL payments
            // this.paymentWalletUsdtAccount = accountInfo.paymentWalletUsdtAccount;
            // this.paymentWalletUsdcAccount = accountInfo.paymentWalletUsdcAccount;
            // this.paymentWalletSolAccount = accountInfo.paymentWallet;

            _balanceData.paused = stateInfo.pause;
            _balanceData.currentStep = stateInfo.currentTier;
            _balanceData.totalTokensSoldSOL = Number(stateInfo.tokensSold) + 1;
            const solUsdtRaised = Number(stateInfo.totalUsdRaised) / 1000000 + 1;
            _balanceData.usdRaised = solUsdtRaised;

            _balanceData.tokenBuyLimit = Number(stateInfo.maxTokensToSell);
            const skipIndexSol: Array<any> = [];
            const soldArray = stateInfo?.tokensLeftUnsold?.toString()?.split(',')
            const solUnsoldArr = skipIndexUpdate(
                soldArray.map((val: any) => Number(val)),
                skipIndexSol,
            );
            _balanceData.unsoldArr = solUnsoldArr
            // console.log('\n **** accountInfo data ****** ', accountInfo);
            // console.log('\n **** stateInfo data ****** ', stateInfo);

            if (this.walletAddressSOL) {
                _balanceDataConnected = await this.getMetaDataSOL(_balanceData);
            }
            return Promise.resolve({ ..._balanceData, ..._balanceDataConnected })
        } catch (error) {
            console.log({ error }, "-eeroor");

            return Promise.resolve(_balanceData)
        }

    }

    /**
   * Get the USDT raised value from Lambda
   */

    getSolprice(): Promise<any> {
        return this.http.get('https://api.diadata.org/v1/assetQuotation/Solana/0x0000000000000000000000000000000000000000').toPromise();
    }

    /**
   * Get the metadata of the connected user
   */
    async getMetaDataSOL(balanceData: IBalnceDataSOL): Promise<IBalnceDataSOL> {
        // === get SOL balance ===== 1 SOL = 1,000,000,000 lamports, this.lamport_per_sol = 1_000_000_000
        balanceData.nativeBalSol = Number(await this.solService.connection.getBalance(this.solService.solWalletPubkey)) / this.solService.lamports_per_sol;

        // === get USDT balance =====

        try {
            // <---------------       USDC BALANCE         -------------------------->
            // getAssociatedTokenAddressSync -> Gets Associated Token Account Address of USDC for that specific User
            const usdcTokenAccount = await getAssociatedTokenAddressSync(this.solService.USDC_TOKEN_ADDRESS, this.solService.solWalletPubkey, true, TOKEN_PROGRAM_ID, this.solService.associate_programId);
            const usdcAccountInfo = await this.solService.connection.getTokenAccountBalance(usdcTokenAccount);
            balanceData.usdcBalSol = usdcAccountInfo.value.uiAmount || 0;
        } catch (e) {
            balanceData.usdcBalSol = 0;
        }

        try {
            // <---------------       USDT BALANCE         -------------------------->
            const usdtTokenAccount = await getAssociatedTokenAddressSync(this.solService.USDT_TOKEN_ADDRESS, this.solService.solWalletPubkey, true, TOKEN_PROGRAM_ID, this.solService.associate_programId);
            const usdtAccountInfo = await this.solService.connection.getTokenAccountBalance(usdtTokenAccount);
            balanceData.usdtBalSol = usdtAccountInfo.value.uiAmount || 0;
        } catch (e) {
            balanceData.usdtBalSol = 0;
        }

        // this.web3PublicKey.findProgramAddressSync returns [Public Key, Size]
        const userInfoAddress = this.solService.web3PublicKey.findProgramAddressSync([this.solService.solWalletPubkey.toBuffer()], this.solService.programId);
        let userInfo;

        try {
            userInfo = await this.solService.program.account.userInfo.fetch(userInfoAddress[0]);
        } catch (e) {
            // console.log("Presale over.", e);
            userInfo = null;
        }
        //  balanceData.userClaimable  = userInfo ? Number(userInfo.value) / LAMPORTS_PER_SOL : 0;
        balanceData.userClaimableSOL = userInfo ? Number(userInfo.value) : 0;
        return Promise.resolve(balanceData)
    }


    // Multi connection Item
    connectWallet(chain: "SOL" | "ETH" | "" = "") {
        this.reownService.connectWallet(chain)
    }

    disconnectWallet() {
        this.reownService.disconnectWallet()
    }

}
